import { cellCount, describeCell, type MediaMatrix as MediaMatrixModel } from '@/lib/blog/media-model';
import { LTR_ISOLATE_ATTRS } from '@/lib/blog/locale-options';
import { cn } from '@/lib/utils';

interface MediaMatrixProps {
  matrix: MediaMatrixModel;
}

/**
 * Screenshot ids down, locales across (Q17).
 *
 * The per-locale panel is the right shape for editing one figure and
 * structurally cannot show that one screenshot is missing its dark variants in
 * eleven languages — which is exactly the gap that ships broken. Incomplete
 * cells are styled as defects rather than warnings because there is no
 * rendering fallback for a missing variant: the same fact blocks a publish.
 */
export function MediaMatrix({ matrix }: MediaMatrixProps) {
  if (matrix.screenshots.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="media-matrix-empty">
        This post references no screenshots.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="media-matrix">
        <caption className="sr-only">
          Image variants present per screenshot and locale, out of four
        </caption>
        <thead>
          <tr className="border-b">
            <th scope="col" className="p-2 text-left">Screenshot</th>
            {matrix.locales.map((locale) => (
              <th key={locale} scope="col" className="p-2 text-center font-mono text-xs">
                <span {...LTR_ISOLATE_ATTRS}>{locale}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.screenshots.map((screenshot) => (
            <tr key={screenshot} className="border-b">
              <th scope="row" className="p-2 text-left font-mono text-xs font-normal">
                <span {...LTR_ISOLATE_ATTRS}>{screenshot}</span>
              </th>
              {matrix.locales.map((locale) => {
                const cell = describeCell(cellCount(matrix, screenshot, locale));
                return (
                  <td
                    key={locale}
                    className={cn(
                      'p-2 text-center tabular-nums',
                      cell.tone === 'defect' && 'bg-destructive/10 font-semibold text-destructive',
                    )}
                    data-tone={cell.tone}
                  >
                    <span aria-hidden="true">{cell.label}</span>
                    <span className="sr-only">
                      {screenshot} {locale}: {cell.srLabel}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
